import React, {useReducer} from 'react'
import Tr from "translations/Translations";
import {makeStyles, TableCell, TableRow} from "@material-ui/core";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import moment from "libs/moment";
import DateFormats from "constants/DateFormats";
import WorkableIconDownloadButton from "components/button/WorkableIconDownloadButton";
import iconWorksReducer from "libs/iconWorksReducer";
import PurchasingGroupStage1Service from "services/PurchasingGroupStage1Service";
import {simpleConfirm} from "libs/ConfirmNotification";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import {PurchasingGroupDocumentStatusLabel} from "../constants/PurchasingGroupDocumentStatus";
import PropTypes from "prop-types";
import PurchasingGroupSectionService from "services/PurchasingGroupSectionService";
import useSnackbar from "hooks/useSnackbar";

const useStyle = makeStyles(theme => ({
    warnIcon: {
        padding: 0,
        color: `${theme.palette.secondary.main} !important`
    },
    descriptionTooltip: {
        whiteSpace: 'pre-line'
    },
    buttonColumn: theme.components.table.buttonColumn,
    versionsCell: {
        background: theme.palette.background.default,
        paddingLeft: 0,
        paddingRight: 0
    }
}));

function MembersDocumentsListTableRow(
    {
        groupId,
        mainDocs,
        editable,
        coordinatorView,
        memberId,
        row,
        index,
        onEditDescriptionFile,
        validationSection,
        allMemberDocumentsApiPath
    }) {
    const classes = useStyle();
    const memberMode = !!memberId;
    const {enqueueErrorSnackbar} = useSnackbar();

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        download: false,
        delete: false,
        publish: false,
        unpublish: false
    });

    const doDelete = () => {
        dispatchIconWorks({type: 'delete', works: true});
        let promise;
        if (!!memberId) {
            promise = PurchasingGroupStage1Service.deleteMemberDocument(groupId, memberId, row.identity)
        } else {
            promise = PurchasingGroupStage1Service.deleteAllMembersDocument(groupId, allMemberDocumentsApiPath, row.identity)
        }
        promise.then(() => PurchasingGroupSectionService.getSections(groupId, validationSection.section)).catch(() => dispatchIconWorks({
            type: 'delete',
            works: false
        }));
    }
    const handleDelete = () => {
        simpleConfirm('confirm_delete_file', doDelete)
    }

    const onDownload = () => {
        dispatchIconWorks({type: 'download', works: true});
        let promise;
        if (!!memberId) {
            promise = PurchasingGroupStage1Service.downloadMemberDocument(groupId, memberId, row.identity, false)
        } else {
            promise = PurchasingGroupStage1Service.downloadAllMembersDocument(groupId, allMemberDocumentsApiPath, row.identity, false)
        }
        promise.finally(() => dispatchIconWorks({type: 'download', works: false}));
    }

    const onUnpublish = () => {
        dispatchIconWorks({type: 'unpublish', works: true});
        PurchasingGroupStage1Service.unpublishAllMembersDocument(groupId, allMemberDocumentsApiPath, row.identity).catch((e) => {
            enqueueErrorSnackbar(`${row.name} - ${row.typeTranslated}: ${e.message}`)
        }).finally(() => dispatchIconWorks({type: 'unpublish', works: false}));
    }
    const onPublish = () => {
        dispatchIconWorks({type: 'publish', works: true});
        PurchasingGroupStage1Service.publishAllMembersDocument(groupId, allMemberDocumentsApiPath, row.identity)
            .catch((e) => {
                enqueueErrorSnackbar(`${row.name} - ${row.typeTranslated}: ${e.message}`)
            })
            .finally(() => dispatchIconWorks({type: 'publish', works: false}));
    }

    return (<TableRow>
        <TableCell>{index}</TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell className={classes.buttonColumn}>
            <WorkableIconSearchButton

                onClick={() => onEditDescriptionFile(row)}
                tooltip={row.description ? <p className={classes.descriptionTooltip}>{row.description}</p> : <i>{Tr('no_description')}</i>}
            />
        </TableCell>
        <TableCell>{row.typeTranslated}</TableCell>
        {!mainDocs && <TableCell>{moment(row.createDate).format(DateFormats.DATETIME_FORMAT)}</TableCell>}
        {mainDocs && <TableCell>{row.publishDate ? moment(row.publishDate).format(DateFormats.DATETIME_FORMAT) : null}</TableCell>}
        {mainDocs && !coordinatorView && <TableCell>{PurchasingGroupDocumentStatusLabel(row.status)}</TableCell>}
        <TableCell className={classes.buttonColumn}>
            <WorkableIconDownloadButton
                onClick={() => onDownload(row.actions.download, row.name)}
                hideTooltip={!row.actions.download}
                disabled={iconWorks.anyWorks || !row.actions.download}
                working={iconWorks.download}
            />
        </TableCell>
        {!coordinatorView && mainDocs && <TableCell className={classes.buttonColumn}>
            {
                row.actions.unpublish ?
                    <WorkableIconUnpublishButton
                        onClick={() => onUnpublish(row.actions.download, row.name)}
                        hideTooltip={!row.actions.unpublish}
                        disabled={!editable || iconWorks.anyWorks || !row.actions.unpublish}
                        working={iconWorks.unpublish}
                    />
                    :
                    <WorkableIconPublishButton
                        onClick={() => onPublish(row.actions.download, row.name)}
                        hideTooltip={!row.actions.publish}
                        disabled={!editable || iconWorks.anyWorks || !row.actions.publish}
                        working={iconWorks.publish}
                    />
            }
        </TableCell>}
        {(!coordinatorView || (coordinatorView && memberMode)) && <TableCell className={classes.buttonColumn}>
            <WorkableIconDeleteButton
                tooltip={row.actions.delete ? Tr('delete') : Tr('delete_repo_disabled')}
                onClick={handleDelete}
                disabled={!editable || iconWorks.anyWorks || !row.actions.delete}
                working={iconWorks.delete}
            />
        </TableCell>}
    </TableRow>)
}

MembersDocumentsListTableRow.propTypes = {
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    mainDocs: PropTypes.bool,
    editable: PropTypes.bool.isRequired,
    coordinatorView: PropTypes.bool.isRequired,
    memberId: PropTypes.number,
    row: PropTypes.shape({
        identity: PropTypes.number,
        actions: PropTypes.object.isRequired,
        publishDate: PropTypes.string,
        createDate: PropTypes.string,
        description: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    onEditDescriptionFile: PropTypes.func.isRequired,
}

export default MembersDocumentsListTableRow;